"use client";

import { useEffect, useRef, useState } from "react";

import { publicApi } from "@/funcs/fetch";
import { getStoredGoogleAdsClickAttribution } from "@/utils/ads/checkout-attribution";

const ADS_ID = "AW-17894586786";
const CONVERSION_LABEL = "gE9GCN_JqOobEKLz5tRC";

type CheckoutSessionSummary = {
  amount_total: number;
  currency: string;
  plan?: string | null;
  billing_cycle?: string | null;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export default function GoogleAdsConversion(props: { sessionId?: string | null }) {
  const hasFiredRef = useRef(false);
  const [summary, setSummary] = useState<CheckoutSessionSummary | null>(null);
  const sessionId = props.sessionId?.trim() || null;

  useEffect(() => {
    if (!sessionId) {
      return;
    }

    let isActive = true;
    publicApi
      .get<CheckoutSessionSummary>(`/checkout-session-summary/${encodeURIComponent(sessionId)}`)
      .then(response => {
        if (isActive) {
          setSummary(response);
        }
      })
      .catch(error => {
        console.error("Failed to load checkout session summary for Google Ads fallback:", error);
      });

    return () => {
      isActive = false;
    };
  }, [sessionId]);

  useEffect(() => {
    if (hasFiredRef.current || !sessionId || !summary) {
      return;
    }

    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      window.gtag = function gtag(...args: unknown[]) {
        window.dataLayer?.push(args);
      };
    }

    window.gtag?.("js", new Date());
    window.gtag?.("config", ADS_ID, { send_page_view: false });

    const { gclid, gbraid, wbraid } = getStoredGoogleAdsClickAttribution();
    const eventParams: Record<string, unknown> = {
      send_to: `${ADS_ID}/${CONVERSION_LABEL}`,
      value: summary.amount_total / 100,
      currency: summary.currency,
      transaction_id: sessionId,
    };

    if (gclid) {
      eventParams.gclid = gclid;
    }
    if (gbraid) {
      eventParams.gbraid = gbraid;
    }
    if (wbraid) {
      eventParams.wbraid = wbraid;
    }

    window.gtag?.("event", "conversion", eventParams);
    hasFiredRef.current = true;
  }, [sessionId, summary]);

  return null;
}
